import { LEVEL_LABELS_TR, type Level, type SeatDto } from '@okn/shared';

const LEVEL_ORDER: Level[] = ['ground', 'side_balcony_left', 'side_balcony_right', 'upper_balcony'];

type Props = {
  allSeats: SeatDto[];
  highlightIds: Set<string>;
};

/**
 * Orientation map for the doorman: for each level that contains the party's
 * seats, plot the true seat coordinates — the whole level faint, the party's
 * seats in crimson — so the host can see at a glance where to walk.
 */
export function MiniSeatMap({ allSeats, highlightIds }: Props) {
  const byLevel = new Map<Level, SeatDto[]>();
  for (const s of allSeats) {
    const arr = byLevel.get(s.level) ?? [];
    arr.push(s);
    byLevel.set(s.level, arr);
  }

  const levels = LEVEL_ORDER.filter((lvl) =>
    (byLevel.get(lvl) ?? []).some((s) => highlightIds.has(s.id)),
  );

  if (levels.length === 0) return null;

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {levels.map((lvl) => (
        <LevelPlot
          key={lvl}
          label={LEVEL_LABELS_TR[lvl]}
          seats={byLevel.get(lvl) ?? []}
          highlightIds={highlightIds}
        />
      ))}
    </div>
  );
}

function LevelPlot({
  label,
  seats,
  highlightIds,
}: {
  label: string;
  seats: SeatDto[];
  highlightIds: Set<string>;
}) {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const s of seats) {
    if (s.posX < minX) minX = s.posX;
    if (s.posY < minY) minY = s.posY;
    if (s.posX > maxX) maxX = s.posX;
    if (s.posY > maxY) maxY = s.posY;
  }
  const pad = 14;
  const w = maxX - minX + pad * 2;
  const h = maxY - minY + pad * 2;
  const hitCount = seats.filter((s) => highlightIds.has(s.id)).length;

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-bg-deep">
      <div className="flex items-baseline justify-between border-b border-border px-3 py-2">
        <span className="kicker">{label}</span>
        <span className="mono text-[11px] text-accent tnum">{hitCount}</span>
      </div>
      <svg
        viewBox={`${minX - pad} ${minY - pad} ${w} ${h}`}
        className="block h-32 w-full"
        role="img"
        aria-label={`${label} koltuk konumu`}
      >
        {/* faint stage hint at the top */}
        {seats.map((s) => {
          const on = highlightIds.has(s.id);
          return (
            <circle
              key={s.id}
              cx={s.posX}
              cy={s.posY}
              r={on ? 4.2 : 1.7}
              fill={on ? 'var(--accent)' : 'var(--seat-available)'}
              opacity={on ? 1 : 0.32}
              style={on ? { filter: 'drop-shadow(0 0 5px var(--accent))' } : undefined}
            />
          );
        })}
      </svg>
    </div>
  );
}
